import React from 'react';
import { 
  Card, 
  CardContent, 
  Typography, 
  Grid, 
  Chip, 
  Tabs, 
  Tab, 
  Accordion, 
  AccordionSummary, 
  AccordionDetails 
} from '@material-ui/core';
import { 
  Build, 
  CheckCircle, 
  Timeline, 
  Warning, 
  ExpandMore,
  Error
} from '@material-ui/icons';
import { Link } from "@backstage/core-components";
import { 
  ProjectPipelineData, 
  PipelineInfo 
} from '../types';
import { 
  getStatusIcon, 
  getStatusChip, 
  formatDuration, 
  getBuildStatusColor 
} from '../utils';
import { TabPanel } from '../shared';

// Pipeline overview cards component
interface PipelineOverviewCardsProps {
  pipelineData: ProjectPipelineData;
}

export const PipelineOverviewCards: React.FC<PipelineOverviewCardsProps> = ({ pipelineData }) => (
  <Grid container spacing={3} style={{ marginBottom: '24px' }}>
    <Grid item xs={12} sm={6} md={3}>
      <Card elevation={2}>
        <CardContent style={{ textAlign: 'center', padding: '20px' }}>
          <Build style={{ fontSize: 40, color: '#2196f3', marginBottom: '8px' }} />
          <Typography variant="h4" style={{ fontWeight: 'bold', color: '#2196f3' }}>
            {pipelineData.totalPipelines}
          </Typography>
          <Typography variant="body2" color="textSecondary">Total Pipelines</Typography>
          <Typography variant="caption" style={{ color: '#4caf50' }}>
            {pipelineData.activePipelines} active
          </Typography>
        </CardContent>
      </Card>
    </Grid>
    <Grid item xs={12} sm={6} md={3}>
      <Card elevation={2}>
        <CardContent style={{ textAlign: 'center', padding: '20px' }}>
          <CheckCircle style={{ fontSize: 40, color: '#4caf50', marginBottom: '8px' }} />
          <Typography variant="h4" style={{ fontWeight: 'bold', color: '#4caf50' }}>
            {pipelineData.overallSuccessRate.toFixed(1)}%
          </Typography>
          <Typography variant="body2" color="textSecondary">Success Rate</Typography>
          <Typography variant="caption" color="textSecondary">
            Overall project rate
          </Typography>
        </CardContent>
      </Card>
    </Grid>
    <Grid item xs={12} sm={6} md={3}>
      <Card elevation={2}>
        <CardContent style={{ textAlign: 'center', padding: '20px' }}>
          <Timeline style={{ fontSize: 40, color: '#ff9800', marginBottom: '8px' }} />
          <Typography variant="h4" style={{ fontWeight: 'bold', color: '#ff9800' }}>
            {pipelineData.totalBuilds}
          </Typography>
          <Typography variant="body2" color="textSecondary">Total Builds</Typography>
          <Typography variant="caption" color="textSecondary">
            Across all pipelines
          </Typography>
        </CardContent>
      </Card>
    </Grid>
    <Grid item xs={12} sm={6} md={3}>
      <Card elevation={2}>
        <CardContent style={{ textAlign: 'center', padding: '20px' }}>
          <Warning style={{ fontSize: 40, color: '#9c27b0', marginBottom: '8px' }} />
          <Typography variant="h4" style={{ fontWeight: 'bold', color: '#9c27b0' }}>
            {formatDuration(pipelineData.averageDuration)}
          </Typography>
          <Typography variant="body2" color="textSecondary">Avg Duration</Typography>
          <Typography variant="caption" color="textSecondary">
            All pipelines
          </Typography>
        </CardContent>
      </Card>
    </Grid>
  </Grid>
);

// Pipeline list component
interface PipelineListProps {
  pipelines: PipelineInfo[];
  onPipelineSelect: (pipeline: PipelineInfo) => void;
}

export const PipelineList: React.FC<PipelineListProps> = ({ pipelines, onPipelineSelect }) => (
  <Grid container spacing={3}>
    {pipelines.map((pipeline: PipelineInfo) => (
      <Grid item xs={12} md={6} lg={4} key={pipeline.id}>
        <Card 
          elevation={1} 
          style={{ 
            cursor: 'pointer',
            transition: 'all 0.2s ease'
          }}
          onClick={() => onPipelineSelect(pipeline)}
        >
          <CardContent>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
              <div style={{ flex: 1 }}>
                <Typography variant="h6" style={{ marginBottom: '4px' }}>
                  {pipeline.name}
                </Typography>
                <Typography variant="body2" color="textSecondary" style={{ marginBottom: '8px' }}>
                  {pipeline.description}
                </Typography>
                {pipeline.pipelineUrl && (
                  <Typography variant="caption" color="primary" style={{ marginBottom: '8px', display: 'block' }}>
                    <Link 
                      to={pipeline.pipelineUrl} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                    >
                      View in Azure DevOps
                    </Link>
                  </Typography>
                )}
              </div>
              {getStatusIcon(pipeline.status)}
            </div>
            
            <div style={{ marginBottom: '12px' }}>
              {getStatusChip(pipeline.status)}
              <Chip 
                size="small" 
                label={pipeline.type} 
                variant="outlined" 
                style={{ marginLeft: '8px' }}
              />
            </div>

            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="caption" color="textSecondary">Success Rate</Typography>
                <Typography variant="body2" style={{ fontWeight: 'bold', color: '#4caf50' }}>
                  {pipeline.successRate.toFixed(1)}%
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="textSecondary">Avg Duration</Typography>
                <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                  {formatDuration(pipeline.averageDuration)}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="textSecondary">Total Builds</Typography>
                <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                  {pipeline.totalBuilds}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="textSecondary">Repositories</Typography>
                <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                  {pipeline.repositories.length}
                </Typography>
              </Grid>
            </Grid>

            <div style={{ marginTop: '12px' }}>
              <Typography variant="caption" color="textSecondary">
                Last run: {pipeline.lastRun ? new Date(pipeline.lastRun).toLocaleString() : 'Never'}
              </Typography>
            </div>
          </CardContent>
        </Card>
      </Grid>
    ))}
  </Grid>
);

// Pipeline tabs component
interface PipelineTabsProps {
  pipelineData: ProjectPipelineData;
  tabValue: number;
  onTabChange: (event: React.ChangeEvent<{}>, newValue: number) => void;
  onPipelineSelect: (pipeline: PipelineInfo) => void;
}

export const PipelineTabs: React.FC<PipelineTabsProps> = ({ 
  pipelineData, 
  tabValue, 
  onTabChange, 
  onPipelineSelect 
}) => (
  <Card>
    <Tabs
      value={tabValue}
      onChange={onTabChange}
      indicatorColor="primary"
      textColor="primary"
      variant="scrollable"
      scrollButtons="auto"
    >
      <Tab label="Pipeline Overview" />
      <Tab label="Repository Mapping" />
      <Tab label="Recent Activity" />
    </Tabs>

    <TabPanel value={tabValue} index={0}>
      {/* Pipeline Overview */}
      <PipelineList pipelines={pipelineData.pipelines} onPipelineSelect={onPipelineSelect} />
    </TabPanel>

    <TabPanel value={tabValue} index={1}>
      {/* Repository Mapping */}
      <div>
        <Typography variant="h6" style={{ marginBottom: '16px' }}>
          Repository-Pipeline Connections
        </Typography>
        {pipelineData.repositories.map((repo: any) => (
          <Accordion key={repo.id}>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                <Typography variant="h6">{repo.name}</Typography>
                <Chip 
                  size="small" 
                  label={`${repo.connectedPipelines} pipelines`} 
                  style={{ 
                    marginRight: '16px',
                    backgroundColor: repo.connectedPipelines > 0 ? '#4caf50' : '#9e9e9e',
                    color: 'white'
                  }}
                />
              </div>
            </AccordionSummary>
            <AccordionDetails>
              <div style={{ width: '100%' }}>
                <Typography variant="body2" color="textSecondary" style={{ marginBottom: '12px' }}>
                  Default Branch: <strong>{repo.defaultBranch}</strong>
                </Typography>
                
                <Typography variant="subtitle2" style={{ marginBottom: '8px' }}>
                  Connected Pipelines:
                </Typography>
                
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {pipelineData.pipelines
                    .filter((p: PipelineInfo) => p.repositories.includes(repo.name))
                    .map((pipeline: PipelineInfo) => (
                      <Card key={pipeline.id} variant="outlined" style={{ padding: '8px', minWidth: '200px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                            {pipeline.name}
                          </Typography>
                          {getStatusChip(pipeline.status)}
                        </div>
                        <Typography variant="caption" color="textSecondary">
                          {pipeline.successRate.toFixed(1)}% success • {pipeline.totalBuilds} builds
                        </Typography>
                        {pipeline.pipelineUrl && (
                          <Typography variant="caption" style={{ display: 'block', marginTop: '4px' }}>
                            <Link 
                              to={pipeline.pipelineUrl} 
                              target="_blank" 
                              rel="noopener noreferrer"
                            >
                              View Pipeline
                            </Link>
                          </Typography>
                        )}
                      </Card>
                    ))}
                </div>
                
                {pipelineData.pipelines.filter((p: PipelineInfo) => p.repositories.includes(repo.name)).length === 0 && (
                  <Typography variant="body2" color="textSecondary">
                    No pipelines connected to this repository
                  </Typography>
                )}
              </div>
            </AccordionDetails>
          </Accordion>
        ))}
      </div>
    </TabPanel>

    <TabPanel value={tabValue} index={2}>
      {/* Recent Activity */}
      <div>
        <Typography variant="h6" style={{ marginBottom: '16px' }}>
          Recent Pipeline Activity
        </Typography>
        {pipelineData.recentActivity.map((activity: {
          pipeline: string;
          action: string;
          time: string;
          user: string;
          duration: string;
        }, index: number) => (
          <Card key={index} style={{ marginBottom: '12px' }} elevation={1}>
            <CardContent style={{ padding: '16px' }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={3}>
                  <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                    {activity.pipeline}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {activity.action.includes('succeeded') ? 
                      <CheckCircle style={{ color: '#4caf50', fontSize: 16 }} /> :
                      <Error style={{ color: '#f44336', fontSize: 16 }} />
                    }
                    <Typography variant="body2">
                      {activity.action}
                    </Typography>
                  </div>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Typography variant="body2" color="textSecondary">
                    {activity.time}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Typography variant="body2" color="textSecondary">
                    {activity.user} • {activity.duration}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        ))}
        {pipelineData.recentActivity.length === 0 && (
          <Typography variant="body2" color="textSecondary" style={{ padding: '20px', textAlign: 'center' }}>
            No recent activity available
          </Typography>
        )}
      </div>
    </TabPanel>
  </Card>
);

// Pipeline detail view component
interface PipelineDetailProps {
  pipeline: PipelineInfo;
}

export const PipelineDetail: React.FC<PipelineDetailProps> = ({ pipeline }) => (
  <div>
    <Card style={{ marginBottom: '20px' }}>
      <CardContent>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
          <div>
            <Typography variant="h4" component="h2" style={{ marginBottom: '8px' }}>
              {pipeline.name}
            </Typography>
            <Typography variant="body1" color="textSecondary" style={{ marginBottom: '8px' }}>
              {pipeline.description}
            </Typography>
            <Typography variant="body2" color="textSecondary" style={{ marginBottom: '8px' }}>
              <strong>Type:</strong> {pipeline.type} • <strong>Path:</strong> {pipeline.path}
            </Typography>
            {pipeline.pipelineUrl && (
              <Typography variant="body2" color="textSecondary">
                <Link 
                  to={pipeline.pipelineUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                >
                  View Pipeline in Azure DevOps
                </Link>
              </Typography>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {getStatusIcon(pipeline.status)}
            {getStatusChip(pipeline.status)}
            <Chip 
              size="small" 
              label={pipeline.enabled ? 'Enabled' : 'Disabled'} 
              style={{ 
                backgroundColor: pipeline.enabled ? '#4caf50' : '#9e9e9e', 
                color: 'white' 
              }}
            />
          </div>
        </div>

        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={3}>
            <div>
              <Typography variant="h6">{pipeline.successRate.toFixed(1)}%</Typography>
              <Typography variant="body2" color="textSecondary">Success Rate</Typography>
            </div>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <div>
              <Typography variant="h6">{pipeline.totalBuilds}</Typography>
              <Typography variant="body2" color="textSecondary">Total Builds</Typography>
            </div>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <div>
              <Typography variant="h6">{formatDuration(pipeline.averageDuration)}</Typography>
              <Typography variant="body2" color="textSecondary">Avg Duration</Typography>
            </div>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <div>
              <Typography variant="h6">{pipeline.repositories.length}</Typography>
              <Typography variant="body2" color="textSecondary">Connected Repositories</Typography>
            </div>
          </Grid>
        </Grid>
      </CardContent>
    </Card>

    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Typography variant="h6" component="h3" style={{ marginBottom: '16px' }}>
              Recent Builds
            </Typography>
            <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
              {pipeline.recentBuilds.map((build: any, index: number) => (
                <div key={index} style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  padding: '12px 0',
                  borderBottom: index < pipeline.recentBuilds.length - 1 ? '1px solid #f0f0f0' : 'none'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div 
                      style={{
                        width: '10px',
                        height: '10px',
                        borderRadius: '50%',
                        backgroundColor: getBuildStatusColor(build.status)
                      }}
                    />
                    <div>
                      <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                        Build #{build.id}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {build.triggeredBy}
                      </Typography>
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <Typography variant="body2">
                      {formatDuration(build.duration)}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {new Date(build.startTime).toLocaleDateString()}
                    </Typography>
                  </div>
                </div>
              ))}
              {pipeline.recentBuilds.length === 0 && (
                <Typography variant="body2" color="textSecondary" style={{ padding: '20px', textAlign: 'center' }}>
                  No recent builds available
                </Typography>
              )}
            </div>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Typography variant="h6" component="h3" style={{ marginBottom: '16px' }}>
              Connected Repositories
            </Typography>
            <div>
              {pipeline.repositories.map((repoName: string, index: number) => (
                <Chip 
                  key={index}
                  label={repoName}
                  variant="outlined"
                  style={{ margin: '4px' }}
                />
              ))}
              {pipeline.repositories.length === 0 && (
                <Typography variant="body2" color="textSecondary">
                  No repositories connected
                </Typography>
              )}
            </div>
            
            <div style={{ marginTop: '20px' }}>
              <Typography variant="subtitle2" style={{ marginBottom: '8px' }}>
                Pipeline Statistics:
              </Typography>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Successful Builds:</Typography>
                  <Typography variant="body2" style={{ color: '#4caf50' }}>
                    {pipeline.successfulBuilds}
                  </Typography>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Failed Builds:</Typography>
                  <Typography variant="body2" style={{ color: '#f44336' }}>
                    {pipeline.failedBuilds}
                  </Typography>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Last Run:</Typography>
                  <Typography variant="body2" color="textSecondary">
                    {pipeline.lastRun ? new Date(pipeline.lastRun).toLocaleString() : 'Never'}
                  </Typography>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  </div>
);